import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Pin, PinOff, Megaphone, BookmarkCheck, Home, Aperture, BarChart3, Video, Users, Activity, ChevronDown, Lightbulb, TrendingUp, Rocket, LayoutDashboard, FileBarChart, Brain, Send, LineChart, Newspaper, Camera, Trophy, Wand2, CalendarDays, History, Compass, Fingerprint, ShoppingBag, Swords, Library, Presentation, LayoutGrid, Music2, Clapperboard } from 'lucide-react';
import { cn } from './ui/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useAudioStudioEnabled } from './audioStudio/queries';

interface AppSidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  isMobile?: boolean;
  onNavigate?: () => void;
  currentPage?: string;
  onPageChange?: (page: string) => void;
  onExpandedChange?: (expanded: boolean) => void;
  /** When false, hide Messages, Brand Pulse, Discover extras (except Creators), Ad Studio. Trends + Evolve require show_evolve. */
  showCowork?: boolean;
  /** Without showCowork: the agent pages Beatly opened for this client team (cowork/clientFeatures.ts). */
  clientPages?: readonly string[];
  /** Saved campaign URL for “Last campaign”; controls visibility and active state. */
  lastCampaignPath?: string | null;
  /** Campaign title for the last-campaign row (from API after load). */
  lastCampaignDisplayName?: string | null;
  pathname?: string;
  /** "Needs you" count on Home (Lykke, cowork/lykke/NeedsYou.ts); 0 hides it. */
  homeBadge?: number;
  /**
   * New layout (cowork/shell/AppShellV2): Pin keeps it open, Float keeps the 64px rail and opens it on hover; the
   * page is pushed either way (the shell follows `onExpandedChange`). Unset = the classic hover sidebar.
   */
  pinMode?: 'pinned' | 'float';
  onPinModeChange?: (mode: 'pinned' | 'float') => void;
}

const userName = 'Noemi Miki';
const _brandName = 'Luminara Beauty';
const userInitials = userName.split(' ').map(n => n[0]).join('').toUpperCase();

function getInitialsColor(initials: string) {
  const colors = [
    { bg: '#EDE9FE', text: '#7C3AED' },
    { bg: '#DBEAFE', text: '#2563EB' },
    { bg: '#D1FAE5', text: '#059669' },
    { bg: '#FEF3C7', text: '#D97706' },
    { bg: '#FCE7F3', text: '#DB2777' },
    { bg: '#E0E7FF', text: '#4F46E5' },
  ];
  const index = initials.charCodeAt(0) % colors.length;
  return colors[index];
}

// Page slug helper
function toPageSlug(label: string): string {
  const map: Record<string, string> = {
    'Home': 'dashboard',
    'Content Hub': 'content-hub',
    'Creators': 'creators',
    'Audience Insights': 'audience-insights',
    'Trends': 'trends',
    'News Monitor': 'news-monitor',
    'Ad Studio': 'ad-studios',
    'Video Ad Studio': 'video-ad-studio',
    'Audio Dubbing': 'audio-dubbing',
    'Audio Studio': 'audio-studio',
    'Create Campaign': 'campaigns',
    'All Campaigns': 'see-campaigns',
    Calendar: 'campaign-calendar',
    'Performance': 'performance',
    'Brand Lift': 'brand-lift',
    'Marketing Mix Model': 'marketing-mix-model-mmm',
    'Awards': 'trophies',
  };
  return map[label] || label.toLowerCase().replace(/\s+/g, '-');
}

type SectionNavItem = {
  label: string;
  icon: typeof BarChart3;
  navKey?: string;
  i18nKey?: string;
};

type CampaignsNavEntry = SectionNavItem | { kind: 'last-campaign' };

function itemNavTarget(item: SectionNavItem): string {
  return item.navKey ?? toPageSlug(item.label);
}

/** Temporarily hidden Discover sub-items (UI only — data kept in section defs). */
const HIDDEN_DISCOVER_ITEM_LABELS = new Set(['Audience Insights', 'News Monitor']);

// Section definitions
// The five client sections (2026-09-28). Agent pages (navKey) show to a client only when Beatly opened that feature
// for the team; staff tools (chats, tasks, approvals, deliverables, desk, canvas, agent team, connections, messages,
// brand wiki) live in the back office, which frames these same pages.
const sections = [
  {
    id: 'BrandOS',
    label: 'Brand Intelligence',
    icon: Compass,
    items: [
      { label: 'Pulse', icon: Activity, navKey: 'agent-brand-pulse' },
      { label: 'Brand Id', icon: Fingerprint, navKey: 'agent-brand-id' },
      { label: 'Promotables', icon: ShoppingBag, navKey: 'agent-brand-promotables' },
      { label: 'Audiences', icon: Users, navKey: 'agent-brand-audiences' },
      { label: 'Competitors', icon: Swords, navKey: 'agent-brand-competitors' },
      { label: 'Library', icon: Library, navKey: 'agent-brand-library' },
    ] satisfies SectionNavItem[],
  },
  {
    id: 'Discover',
    label: 'Discover',
    icon: Rocket,
    items: [
      { label: 'Creators', icon: Users },
      { label: 'Competitor ads', icon: Megaphone, navKey: 'agent-competitor-ads' },
      { label: 'Ads Library', icon: Library, navKey: 'agent-ads-library' },
      { label: 'Radar library', icon: BookmarkCheck, navKey: 'agent-radar-library' },
      { label: 'TikTok trends', icon: TrendingUp, navKey: 'agent-tiktok-trends' },
      { label: 'Breakout formats', icon: Rocket, navKey: 'agent-breakout-formats' },
      { label: 'Audience Insights', icon: Lightbulb },
      { label: 'News Monitor', icon: Newspaper },
    ],
  },
  {
    id: 'Create',
    label: 'Create',
    icon: Aperture,
    items: [
      { label: 'Deck studio', icon: Presentation, navKey: 'agent-reports' },
      // Client launch (Nov 2026): Ad Studio (the landing) shows to a client team with audio or video, Video Studio with
      // video, Audio Studio with audio (cowork/clientFeatures.ts). The mock pages (Video Ad Studio, Audio Dubbing)
      // stay staff only.
      { label: 'Ad Studio', icon: Wand2, navKey: 'ad-studios' },
      { label: 'Video Studio', icon: Clapperboard, navKey: 'agent-video-studio' },
      { label: 'Video Ad Studio', icon: Video },
      { label: 'Audio Dubbing', icon: Video },
      { label: 'Audio Studio', icon: Music2, navKey: 'audio-studio' },
    ],
  },
  {
    id: 'Campaigns',
    label: 'Activate',
    icon: Send,
    items: [
      { label: 'All Campaigns', icon: BarChart3 },
      { label: 'Calendar', icon: CalendarDays },
      { label: 'Create Campaign', icon: Rocket },
    ] satisfies SectionNavItem[],
  },
  {
    id: 'Evolve',
    label: 'Evolve',
    icon: LineChart,
    items: [
      { label: 'Performance', icon: LayoutDashboard },
      { label: 'Brand Lift', icon: FileBarChart },
      { label: 'Marketing Mix Model', icon: Brain },
      { label: 'Awards', icon: Trophy },
      { label: 'Dashboards', icon: LayoutGrid, navKey: 'agent-dashboards' },
    ],
  },
];

// Messages is built in the back office first (client-facing later); the older Brand Pulse page is replaced by Brand OS → Pulse.
const quickNavItems = [
  { icon: Home, label: 'Home', page: 'dashboard' },
  { icon: Video, label: 'Content Hub', page: 'content-hub' },
];

// eslint-disable-next-line react-refresh/only-export-components -- exported for its tests
export function buildVisibleSections(showCowork: boolean, showEvolveNav: boolean, clientPages: readonly string[] = []) {
  const keep = (sectionId: string, item: SectionNavItem): boolean => {
    // Evolve's platform pages need the account's Evolve entitlement (show_evolve), staff included.
    if (sectionId === 'Evolve' && !item.navKey) return showEvolveNav;
    if (showCowork) return true;
    // Clients: agent pages only when Beatly opened that feature for the team.
    if (item.navKey) return clientPages.includes(itemNavTarget(item));
    // The Ad Studio mock pages are not for clients.
    if (sectionId === 'Create') return false;
    // Discover: Creators always; Trends only with show_evolve.
    if (sectionId === 'Discover') return item.label === 'Creators' || (item.label === 'Trends' && showEvolveNav);
    return true;
  };
  return sections.flatMap((sec) => {
    const items = sec.items.filter((item) => keep(sec.id, item as SectionNavItem));
    return items.length ? [{ ...sec, items }] : [];
  });
}

/** Audio Studio follows `useAudioStudioAccess` (staff, or a client team with `audio`): drop its entry for everyone else. */
function withoutAudioStudio<T extends { items: readonly { label: string }[] }>(secs: T[], show: boolean): T[] {
  return show ? secs : secs.map((sec) => ({ ...sec, items: sec.items.filter((item) => item.label !== 'Audio Studio') }));
}

/** Map creator-embed sub-routes so Discover → Creators stays highlighted. */
function navPageForCreatorsSidebar(page: string): string {
  if (page === 'search-results' || page === 'creator-profile' || page === 'discovery-creatorlist') {
    return 'creators';
  }
  return page;
}

// Which section a page belongs to (within the given section list)
function getActiveSectionId(page: string, sectionList: typeof sections): string | null {
  const p = navPageForCreatorsSidebar(page);
  for (const section of sectionList) {
    if (section.items.some((item) => itemNavTarget(item as SectionNavItem) === p)) {
      return section.id;
    }
  }
  return null;
}

export function AppSidebar({
  isCollapsed: _isCollapsed,
  onToggle: _onToggle,
  isMobile = false,
  onNavigate,
  currentPage,
  onPageChange,
  onExpandedChange,
  showCowork = false,
  lastCampaignPath = null,
  lastCampaignDisplayName = null,
  pathname = '',
  clientPages = [],
  homeBadge = 0,
  pinMode,
  onPinModeChange,
}: AppSidebarProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const showEvolveNav = user?.show_evolve === true;
  const audioStudio = useAudioStudioEnabled();
  const visibleSections = useMemo(
    () => withoutAudioStudio(buildVisibleSections(showCowork, showEvolveNav, clientPages), audioStudio),
    [showCowork, showEvolveNav, clientPages, audioStudio],
  );

  const [expandedSections, setExpandedSections] = useState<string[]>(() =>
    buildVisibleSections(showCowork, showEvolveNav, clientPages).map((s) => s.id),
  );
  const [hovered, setHovered] = useState(false);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const settingsMenuRef = useRef<HTMLDivElement>(null);
  const settingsBtnRef = useRef<HTMLButtonElement>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const pinned = pinMode === 'pinned';
  const expanded = isMobile || hovered || pinned;
  const initialsColor = getInitialsColor(userInitials);

  useEffect(() => {
    setExpandedSections(visibleSections.map((s) => s.id));
  }, [visibleSections]);

  // Notify parent when expanded state changes
  useEffect(() => {
    if (!isMobile) {
      onExpandedChange?.(hovered || pinned);
    }
  }, [hovered, pinned, isMobile, onExpandedChange]);

  const handleMouseEnter = () => {
    if (isMobile) return;
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setHovered(true);
  };

  const handleMouseLeave = () => {
    if (isMobile) return;
    hoverTimeoutRef.current = setTimeout(() => {
      setHovered(false);
      setShowSettingsMenu(false);
    }, 250);
  };

  useEffect(() => {
    if (!showSettingsMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        settingsMenuRef.current && !settingsMenuRef.current.contains(e.target as Node) &&
        settingsBtnRef.current && !settingsBtnRef.current.contains(e.target as Node)
      ) {
        setShowSettingsMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSettingsMenu]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setProfileImage(ev.target?.result as string);
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(s => s !== sectionId)
        : [...prev, sectionId]
    );
  };

  const navigate = (page: string) => {
    // Cowork is an app mode rather than a platform page — route by URL.
    if (page === 'cowork') {
      window.history.pushState({}, '', '/cowork');
      window.dispatchEvent(new PopStateEvent('popstate'));
      onNavigate?.();
      return;
    }
    onPageChange?.(page);
    onNavigate?.();
  };

  const _Avatar = ({ size = 40, className = '' }: { size?: number; className?: string }) => (
    <div
      className={cn('relative rounded-full flex-shrink-0 group/avatar cursor-pointer', className)}
      style={{ width: size, height: size }}
      onClick={() => fileInputRef.current?.click()}
      title={t('nav.uploadProfilePhoto')}
    >
      {profileImage ? (
        <img src={profileImage} alt={userName} className="w-full h-full rounded-full object-cover" />
      ) : (
        <div
          className="w-full h-full rounded-full flex items-center justify-center select-none"
          style={{ backgroundColor: initialsColor.bg, color: initialsColor.text, fontSize: size * 0.38 }}
        >
          <span style={{ fontWeight: 600, letterSpacing: '0.02em' }}>{userInitials}</span>
        </div>
      )}
      <div className="absolute inset-0 rounded-full bg-black/0 group-hover/avatar:bg-black/40 flex items-center justify-center transition-all">
        <Camera
          className="text-white opacity-0 group-hover/avatar:opacity-100 transition-opacity"
          style={{ width: size * 0.38, height: size * 0.38 }}
        />
      </div>
    </div>
  );

  return (
    <aside
      className={cn(
        'flex flex-col bg-white',
        isMobile ? 'w-full h-full' : pinMode ? 'fixed left-0 top-14 bottom-0 transition-[width] duration-[180ms] ease-[cubic-bezier(0.2,0,0,1)]' : 'fixed left-0 top-14 bottom-0 transition-all duration-300',
        !isMobile && (expanded ? 'w-64 z-50' : 'w-16 z-40'),
        pinMode && 'bg-[var(--shell-bg,#fff)]',
      )}
      data-app-sidebar
      data-pin-mode={pinMode}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleImageUpload}
      />

      {/* Scrollable navigation */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 px-2 space-y-0.5">
        {/* Quick nav items */}
        {quickNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = currentPage === item.page;
          return (
            <button
              key={item.label}
              onClick={() => navigate(item.page)}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors',
                isActive
                  ? 'bg-[#be43ff]/8 text-[#be43ff]'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              )}
              title={!expanded ? item.label : undefined}
            >
              <span className="relative flex-shrink-0">
                <Icon className="w-5 h-5" />
                {item.page === 'dashboard' && homeBadge > 0 && !expanded && (
                  <span className="absolute -right-1.5 -top-1.5 grid h-4 min-w-4 place-items-center rounded-full bg-[#be43ff] px-1 text-[10px] font-bold leading-none text-white" aria-label={`${homeBadge} need you`}>{homeBadge > 9 ? '9+' : homeBadge}</span>
                )}
              </span>
              {expanded && <span className="text-sm whitespace-nowrap">{item.label}</span>}
              {expanded && item.page === 'dashboard' && homeBadge > 0 && (
                <span className="ml-auto grid h-5 min-w-5 place-items-center rounded-full bg-[#be43ff] px-1.5 text-[11px] font-bold text-white" title="Needs you">{homeBadge}</span>
              )}
            </button>
          );
        })}

        {/* Divider */}
        <div className="!my-2.5 mx-1 border-t border-gray-100" />

        {/* Sections with sub-items */}
        {visibleSections.map((section) => {
          const visibleItems =
            section.id === 'Discover'
              ? section.items.filter((item) => !HIDDEN_DISCOVER_ITEM_LABELS.has(item.label))
              : section.items;
          const navEntries: CampaignsNavEntry[] =
            section.id === 'Campaigns' && lastCampaignPath && visibleItems.length > 0
              ? [
                  visibleItems[0] as SectionNavItem,
                  { kind: 'last-campaign' as const },
                  ...(visibleItems.slice(1) as SectionNavItem[]),
                ]
              : (visibleItems as SectionNavItem[]);
          const SectionIcon = section.icon;
          const isExpanded = expandedSections.includes(section.id);
          const isActiveSection = getActiveSectionId(currentPage || '', visibleSections) === section.id;
          const firstItemSlug =
            visibleItems.length > 0 ? itemNavTarget(visibleItems[0] as SectionNavItem) : 'dashboard';

          return (
            <div key={section.id}>
              {/* Section header */}
              <button
                onClick={() => {
                  if (expanded) {
                    toggleSection(section.id);
                  } else {
                    // When collapsed, clicking section icon navigates to first visible item
                    navigate(firstItemSlug);
                  }
                }}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors',
                  isActiveSection && !expanded
                    ? 'bg-[#be43ff]/8 text-[#be43ff]'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                )}
                title={!expanded ? section.label : undefined}
              >
                <SectionIcon className="w-5 h-5 flex-shrink-0" />
                {expanded && (
                  <>
                    <span className="text-xs uppercase tracking-wider flex-1 text-left whitespace-nowrap" style={{ fontWeight: 600 }}>
                      {section.label}
                    </span>
                    <ChevronDown
                      className={cn(
                        'w-3.5 h-3.5 transition-transform shrink-0',
                        isExpanded ? '' : '-rotate-90'
                      )}
                    />
                  </>
                )}
              </button>

              {/* Sub-items (only when expanded sidebar + section open) */}
              {expanded && isExpanded && (
                <div className="mt-0.5 mb-1 space-y-0.5">
                  {navEntries.map((entry) => {
                    if ('kind' in entry && entry.kind === 'last-campaign') {
                      const isActive = Boolean(lastCampaignPath && pathname === lastCampaignPath);
                      const label = lastCampaignDisplayName?.trim() || t('nav.lastCampaign');
                      return (
                        <button
                          key="last-campaign"
                          type="button"
                          onClick={() => navigate('last-campaign')}
                          title={label}
                          className={cn(
                            'w-full flex items-center gap-2 pl-14 pr-3 py-1.5 rounded-lg text-xs transition-colors',
                            isActive
                              ? 'bg-[#be43ff]/8 text-[#be43ff]'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          )}
                        >
                          <History className={cn('w-4 h-4 flex-shrink-0', isActive ? 'text-[#be43ff]' : 'text-gray-400')} />
                          <span className="min-w-0 flex-1 truncate text-left font-medium">{label}</span>
                        </button>
                      );
                    }
                    const item = entry as SectionNavItem;
                    const navItem = item;
                    const ItemIcon = item.icon;
                    const target = itemNavTarget(navItem);
                    const displayLabel = navItem.i18nKey ? t(navItem.i18nKey) : item.label;
                    const navPage = navPageForCreatorsSidebar(currentPage || '');
                    let isActive: boolean;
                    if (target === 'see-campaigns') {
                      /* List only — campaign detail highlights last-campaign row or nothing */
                      isActive = pathname === '/campaigns';
                    } else {
                      isActive = navPage === target;
                    }
                    const key = navItem.navKey ?? item.label;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => navigate(target)}
                        className={cn(
                          'w-full flex items-center gap-3 pl-11 pr-3 py-1.5 rounded-lg text-sm transition-colors',
                          isActive
                            ? 'bg-[#be43ff]/8 text-[#be43ff]'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        )}
                      >
                        <ItemIcon className={cn('w-4 h-4 flex-shrink-0', isActive ? 'text-[#be43ff]' : 'text-gray-400')} />
                        <span className="whitespace-nowrap">{displayLabel}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* New layout: Pin / Float, the last row (quiet: a preference, not a place) */}
      {pinMode && !isMobile && onPinModeChange && (
        <div className="px-2 pb-3 pt-1">
          <button
            type="button"
            data-testid="sidebar-mode-toggle"
            aria-pressed={pinned}
            onClick={() => onPinModeChange(pinned ? 'float' : 'pinned')}
            title={pinned ? 'Float the sidebar: a slim rail that opens when you point at it' : 'Pin the sidebar open'}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-800"
          >
            {pinned ? <PinOff className="h-5 w-5 shrink-0" /> : <Pin className="h-5 w-5 shrink-0" />}
            {expanded && <span className="whitespace-nowrap text-sm">{pinned ? 'Float sidebar' : 'Pin sidebar'}</span>}
          </button>
        </div>
      )}
    </aside>
  );
}